import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import diamant from "./assets/diamant.png";
import line from "./assets/line.png";
import gradient from "./assets/gradient.png";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./InstantTournaments.module.scss";
import {
  MatchTournamentType,
  TournamentPlayerPerTeam,
  TournamentStatus,
} from "../../../graphql/generated";

const FilterSection = ({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: {
    label: string;
    value:
      | TournamentPlayerPerTeam[]
      | TournamentStatus[]
      | MatchTournamentType[];
  }[];
  selected:
    | TournamentPlayerPerTeam[]
    | TournamentStatus[]
    | MatchTournamentType[];
  onChange: (value: {
    label: string;
    value:
      | TournamentPlayerPerTeam[]
      | TournamentStatus[]
      | MatchTournamentType[];
  }) => void;
}) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <div className="py-2">
      <button
        className="w-full flex justify-between items-center text-sm font-semibold text-white"
        onClick={() => setOpen(!open)}
      >
        <span>{t(title)}</span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} size="sm" />
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {options.map((option) => (
            <button
              key={option.label}
              className={classNames(
                "w-full text-left text-sm px-3 py-2 rounded-md",
                selected?.[0] === option?.value?.[0]
                  ? "bg-gradient-to-r from-blue-800 to-blue-600 text-white"
                  : "text-gray-400 hover:bg-white/10"
              )}
              style={{
                background:
                  selected?.[0] === option?.value?.[0]
                    ? "linear-gradient(180deg, #262F40 0%, #1D2430 100%)"
                    : "transparent",
                borderTop:
                  selected?.[0] === option?.value?.[0]
                    ? "1px solid rgba(0, 174, 239, 0.3)"
                    : "none",
                boxShadow:
                  selected?.[0] === option?.value?.[0]
                    ? "0px 0px 6px 0px rgba(91, 91, 91, 0.15) inset"
                    : "none",
                color:
                  selected?.[0] === option?.value?.[0] ? "#ffffff" : "#727587",
              }}
              onClick={() => onChange(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const TournamentFilter = ({
  onChangeStatus,
  onChangeFormat,
  onChangeType,
  currentStatus,
  currentFormat,
  tournamentType,
}: {
  onChangeStatus: (status: TournamentStatus[]) => void;
  onChangeFormat: (format: TournamentPlayerPerTeam[]) => void;
  onChangeType: (type: MatchTournamentType[]) => void;
  currentStatus: TournamentStatus[];
  currentFormat: TournamentPlayerPerTeam[];
  tournamentType: MatchTournamentType[];
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TournamentStatus[]>(currentStatus);
  const [format, setFormat] = useState<TournamentPlayerPerTeam[]>(
    currentFormat
  );
  const [type, setType] = useState<MatchTournamentType[]>(tournamentType);

  const statusOptions = [
    { label: t("all"), value: [] },
    {
      label: t("instant_tournament.upcoming"),
      value: [TournamentStatus.Upcoming],
    },
    {
      label: t("instant_tournament.ongoing"),
      value: [TournamentStatus.Ongoing],
    },
    {
      label: t("instant_tournament.completed"),
      value: [TournamentStatus.Completed],
    },
  ];

  const formatOptions = [
    { label: t("all"), value: [] },
    { label: "1x1", value: [TournamentPlayerPerTeam.OneVsOne] },
    { label: "2x2", value: [TournamentPlayerPerTeam.TwoVsTwo] },
  ];

  const typeOptions = [
    { label: t("all"), value: [] },
    {
      label: t("instant_tournament.scheduled_tournaments"),
      value: [MatchTournamentType.Scheduled],
    },
    {
      label: t("instant_tournament.instant_tournaments"),
      value: [MatchTournamentType.Instant],
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className={classNames(
          "flex items-center justify-start gap-2  w-full text-white rounded-lg py-1 px-1 font-semibold relative",
          styles.filterTitle
        )}
      >
        <div className="flex items-center gap-2">
          <img
            src={diamant}
            alt="icon"
            width="2rem"
            height="2rem"
            className="w-5 h-5"
          />
          <span>Filter Matches</span>
        </div>
        <img src={line} alt="icon" className={styles.line} />
        <img src={gradient} alt="icon" className={styles.gradient} />
      </div>

      <div className="flex flex-col gap-3">
        <FilterSection
          title="instant_tournament.filter.status"
          options={statusOptions}
          selected={status}
          onChange={(value) => {
            setStatus(value.value as TournamentStatus[]);
            onChangeStatus(value.value as TournamentStatus[]);
          }}
        />
        <div className={styles.filterDivider} />
        <FilterSection
          title="type"
          options={typeOptions}
          selected={type}
          onChange={(value) => {
            setType(value.value as MatchTournamentType[]);
            onChangeType(value.value as MatchTournamentType[]);
          }}
        />
        <div className={styles.filterDivider} />
        <FilterSection
          title="instant_tournament.filter.format"
          options={formatOptions}
          selected={format}
          onChange={(value) => {
            setFormat(value.value as TournamentPlayerPerTeam[]);
            onChangeFormat(value.value as TournamentPlayerPerTeam[]);
          }}
        />
      </div>
    </div>
  );
};
